export type ErrorContext =
  | "general"
  | "login"
  | "payment"
  | "upload"
  | "accessRequest"
  | "verification"
  | "ads"
  | "budget"
  | "bid"
  | "support";

type ErrorOptions = {
  context?: ErrorContext;
  /** Application-authored copy only. Never pass a response field as fallback. */
  fallback?: string;
};

const DEFAULT_MESSAGE = "Não foi possível concluir esta ação. Tente novamente em instantes.";
const CONNECTION = "Não foi possível conectar ao DaKasa. Confira sua conexão e tente novamente.";
const TIMEOUT =
  "A resposta demorou mais que o esperado. Confira se a ação foi concluída antes de tentar novamente.";

const messages: Record<string, string> = {
  "current password is invalid": "Senha atual incorreta. Confira e tente novamente.",
  "email already in use": "Este e-mail já está em uso. Confira o endereço ou use outro e-mail.",
  "collaborator already has an account":
    "Este colaborador já possui uma conta. Confira o acesso existente antes de enviar outro convite.",
  upload_finalizing:
    "Ainda não foi possível confirmar o recebimento do arquivo. Aguarde um instante e confira o envio antes de tentar novamente.",
  already_last_bidder:
    "Sua empresa já tem o último lance nesta postagem. Aguarde outro lance para participar novamente.",
  bid_cooldown: "Aguarde 10 segundos antes de enviar outro lance nesta postagem.",
  bid_not_allowed:
    "Sua empresa não pode anunciar nesta postagem. Escolha outra postagem disponível.",
  bid_moved: "O valor dessa publicação mudou. Atualize as publicações antes de fazer outro lance.",
  bid_tax_moved: "A taxa desse lance mudou. Atualize as publicações antes de fazer outro lance.",
  bid_amount_limit:
    "O valor desse lance ultrapassa o limite permitido. Escolha um valor menor antes de tentar novamente.",
  // The minimum depends on the company (R$10, or R$3 for DaKasa's own), so the
  // copy names no amount.
  bid_increment_below_minimum:
    "O aumento desse lance está abaixo do mínimo permitido para a sua empresa. Escolha uma das fichas disponíveis e tente novamente.",
  negotiation_closed:
    "As negociações estão fechadas. Aguarde o próximo período para enviar lances.",
  negotiation_not_ready:
    "A empresa precisa concluir as condições para negociar. Confira o anúncio, o perfil e a forma de pagamento.",
  negotiation_balance_insufficient:
    "O Saldo de Negociações não cobre este lance. Recarregue para continuar.",
  negotiation_balance_restricted:
    "Saldo de Negociações bloqueado por devolução de Pix. Recarregue para regularizar.",
  negotiation_balance_disabled:
    "O Saldo de Negociações está temporariamente indisponível para lances.",
  rate_limit: "Recebemos muitas tentativas. Aguarde alguns minutos antes de tentar novamente.",
  rate_limit_exceeded:
    "Recebemos muitas tentativas. Aguarde alguns minutos antes de tentar novamente.",
  invalid_credentials: "E-mail ou senha incorretos. Confira os dados e tente novamente.",
  session_expired: "Sua sessão expirou. Entre novamente para continuar.",
  token_expired: "Sua sessão expirou. Entre novamente para continuar.",
  forbidden: "Você não tem permissão para esta ação. Fale com um administrador da empresa.",
  permission_denied: "Você não tem permissão para esta ação. Fale com um administrador da empresa.",
  cnpj_already_registered:
    "Já existe uma empresa cadastrada com este CNPJ. Se precisar recuperar o acesso, fale com nossa equipe.",
  company_not_active:
    "Este CNPJ não está ativo na Receita Federal. Regularize a situação cadastral antes de solicitar acesso.",
  registry_unavailable:
    "A consulta do CNPJ está indisponível no momento. Tente novamente em instantes.",
  registry_incomplete:
    "Não recebemos todos os dados da empresa na consulta do CNPJ. Tente novamente; se persistir, fale com nossa equipe.",
  invalid_submission_id:
    "Não foi possível identificar esta solicitação. Reabra a página de solicitação de acesso e confira os dados antes de enviar.",
  submission_payload_conflict:
    "Esta solicitação já foi enviada com outros dados. Confira o envio anterior antes de iniciar uma nova solicitação.",
  provider_unavailable:
    "A verificação está temporariamente indisponível. Tente novamente em instantes.",
  consent_required: "Confirme o consentimento antes de iniciar a verificação.",
  backlog_full:
    "Há muitos envios aguardando processamento. Aguarde um instante antes de tentar novamente.",
  too_many_uploads:
    "Há muitos envios aguardando processamento. Aguarde um instante antes de tentar novamente.",
  unsupported_media_type: "Este formato de arquivo não é aceito. Escolha um arquivo compatível.",
  file_too_large: "O arquivo ultrapassa o tamanho permitido. Escolha um arquivo menor.",
  upload_cancelled: "O envio foi cancelado. Se quiser continuar, selecione o arquivo novamente.",
  // Thrown by the ads route loaders (src/routes/loaders/paymentMethodGate.ts)
  // when the organization's
  // payment readiness could not be read. It is an unknown answer, not a
  // missing permission and not a missing payment method.
  payment_readiness_unavailable:
    "Não foi possível verificar agora a forma de pagamento da empresa. Recarregue a página em instantes; se continuar, fale com o suporte.",
};

// Legacy services sometimes return only a message. Exact aliases, never arbitrary
// message passthrough or substring matching, preserve known validation semantics.
const aliases: Record<string, string> = {
  "your org is the last bidder": "already_last_bidder",
  same_org: "already_last_bidder",
  cooldown: "bid_cooldown",
  wait: "bid_cooldown",
  "wait 10 seconds to make a new bid": "bid_cooldown",
  "you cannot bid on this post": "bid_not_allowed",
  "your org is marked as adult only and cannot bid on this post": "bid_not_allowed",
  "network error": "network_error",
  "failed to fetch": "network_error",
  "load failed": "network_error",
  "email and applicantemail must be valid": "invalid_email",
  "applicantphone must be a valid phone number": "invalid_phone",
  "an organization already exists for this cnpj": "cnpj_already_registered",
  "company registry status is not active": "company_not_active",
};
messages.invalid_email = "Confira o e-mail da empresa e o e-mail do representante antes de enviar.";
messages.invalid_phone = "Confira o DDD e o telefone do representante antes de enviar.";

function record(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === "object"
    ? (value as Record<string, unknown>)
    : undefined;
}

/** Status is transport metadata, never a business `code` or a nested provider code. */
export function getErrorStatus(error: unknown): number | undefined {
  const root = record(error);
  const response = record(root?.response);
  const value = response?.status ?? root?.status ?? root?.statusCode ?? root?.httpStatus;
  const status =
    typeof value === "number"
      ? value
      : typeof value === "string" && /^\d{3}$/.test(value)
        ? Number(value)
        : undefined;
  return status !== undefined && (status === 0 || (status >= 100 && status <= 599))
    ? status
    : undefined;
}

/** Decode only known envelopes; bounded work prevents cyclic/oversized errors from crashing UI. */
function errorSignals(error: unknown) {
  const codes: string[] = [];
  const seen = new Set<unknown>();
  let visits = 0;
  function visit(value: unknown, depth: number) {
    if (depth > 8 || ++visits > 64 || value == null) return;
    if (typeof value === "string") {
      if (value.length > 16384) return;
      const clean = value.trim();
      if (clean.startsWith("{") || clean.startsWith("[")) {
        try {
          visit(JSON.parse(clean), depth + 1);
        } catch {
          /* Untrusted text is not display copy. */
        }
        return;
      }
      const normalized = clean.toLowerCase();
      const code = Object.prototype.hasOwnProperty.call(aliases, normalized)
        ? aliases[normalized]
        : normalized;
      codes.push(code);
      return;
    }
    const data = record(value);
    if (!data || seen.has(value)) return;
    seen.add(value);
    if (Array.isArray(value)) {
      value.slice(0, 10).forEach((item) => visit(item, depth + 1));
      return;
    }
    for (const key of [
      "code",
      "reasonCode",
      "reason",
      "response",
      "responseBody",
      "data",
      "error",
      "errors",
      "msg",
      "message",
      "detail",
      "details",
      "cause",
      "last_payment_error",
    ]) {
      if (data[key] !== undefined) visit(data[key], depth + 1);
    }
  }
  visit(error, 0);
  return { codes };
}

/** Only the documented password rejection may count toward wrong-password attempts. */
export function isCurrentPasswordIncorrect(error: unknown): boolean {
  return (
    getErrorStatus(error) === 403 &&
    errorSignals(error).codes.includes("current password is invalid")
  );
}

/** The only remote-error-to-copy boundary. The original error remains unchanged. */
export function getUserFacingError(
  error: unknown,
  { context = "general", fallback = DEFAULT_MESSAGE }: ErrorOptions = {},
): string {
  const status = getErrorStatus(error);
  // /authenticate deliberately returns 403 for invalid credentials as well as
  // unavailable accounts. A generic legacy "Forbidden" body must not override
  // this endpoint's context with an unrelated organization-permission message.
  if (context === "login" && (status === 401 || status === 403))
    return messages.invalid_credentials;
  const { codes } = errorSignals(error);
  for (const code of codes) {
    if (Object.prototype.hasOwnProperty.call(messages, code)) return messages[code];
  }
  if (codes.some((code) => ["econnaborted", "etimedout", "timeout"].includes(code))) return TIMEOUT;
  const root = record(error);
  if (
    status === 0 ||
    codes.some((code) => ["err_network", "network_error"].includes(code)) ||
    (root?.isAxiosError === true && !root?.response)
  )
    return CONNECTION;
  if (status === 401) return messages.session_expired;
  if (status === 403) return messages.forbidden;
  if (status === 408 || status === 504) return TIMEOUT;
  if (status === 429) return messages.rate_limit;
  if (status === 413)
    return context === "upload"
      ? messages.file_too_large
      : "Os dados enviados ultrapassam o tamanho permitido. Reduza os anexos ou o conteúdo e tente novamente.";
  if (status === 415) return messages.unsupported_media_type;
  if (status === 507 && context === "ads")
    return "Você atingiu o limite de anúncios. Remova um anúncio que não usa mais antes de criar outro.";
  if (status === 404)
    return context === "ads"
      ? "Este anúncio não foi encontrado. Atualize a lista e tente novamente."
      : "Este item não está mais disponível. Atualize a página e tente novamente.";
  if (status === 409)
    return "Esta ação conflita com o estado atual dos dados. Atualize a página e confira o resultado antes de tentar novamente.";
  if (status === 400 || status === 422) {
    if (context === "payment") return "Confira os dados da cobrança e tente novamente.";
    if (context === "budget") return "Informe um limite de orçamento válido, maior que zero.";
    if (context === "ads")
      return "Confira os campos e a mídia do anúncio antes de tentar novamente.";
    if (context === "accessRequest")
      return "Confira o CNPJ, os contatos e os campos obrigatórios antes de enviar a solicitação.";
    if (context === "support") return "Confira o título, a mensagem e os anexos antes de enviar.";
    return "Revise os dados informados e tente novamente.";
  }
  return fallback;
}
